import { FC, ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as DOMPurify from "dompurify";
import BtButton from "../../../ts-components/bt-button/bt-button";
import { ServiceEmailPreview, EmailPreviewType, EmailPreviewParams } from "../../../services/service-email-preview";

type Props = {
  type: EmailPreviewType,
  params: EmailPreviewParams,
  children?: ReactNode,
};

const MESSAGE_SLOT = '<slot name="message"></slot>';

const PREVIEW_STYLE = '<style>:host{display:block}.email-wrapper{width:100% !important;max-width:560px}</style>';

const toShadowMarkup = (html: string) =>
  DOMPurify.sanitize(html, { WHOLE_DOCUMENT: true, ADD_TAGS: ['slot'] });

const EmailPreview: FC<Props> = ({ type, params, children }) => {
  const [t] = useTranslation('shared', { keyPrefix: 'emailPreview' });
  const [html, setHtml] = useState("");
  const [failed, setFailed] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const paramsJson = JSON.stringify(params);
  const requestSeq = useRef(0);
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const seq = ++requestSeq.current;
    const timer = setTimeout(async () => {
      try {
        const preview = await ServiceEmailPreview.getPreview(type, JSON.parse(paramsJson));
        if (seq === requestSeq.current) {
          setHtml(preview.html);
          setFailed(false);
        }
      } catch (error) {
        console.error("Failed to render the email preview", error);
        if (seq === requestSeq.current) setFailed(true);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [type, paramsJson, retryToken]);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const root = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
    root.innerHTML = PREVIEW_STYLE + (html && !failed ? toShadowMarkup(html) : MESSAGE_SLOT);
  }, [html, failed]);

  return (
    <div>
      {failed && (
        <div style={{
          border: "1px solid var(--gray-300)", borderRadius: "6px", background: "#F3F4F6", marginBottom: "16px",
          padding: "24px", display: "flex", flexDirection: "column", alignItems: "center", gap: "12px",
        }}>
          <span>{t('error', 'Förhandsgranskningen kunde inte uppdateras.')}</span>
          <BtButton class="white" size="xxs" onClick={() => setRetryToken(token => token + 1)}>
            {t('retry', 'Försök igen')}
          </BtButton>
        </div>
      )}
      <div ref={hostRef}>
        {children ? <div slot="message">{children}</div> : null}
      </div>
    </div>
  );
};

export default EmailPreview;
